import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import ApplyJobModal from "./ApplyJobModal";
import axios from "axios";

const api = axios.create({
  baseURL: 'https://localhost:7022',
  headers: {
    accept: "*/*",
    // Authorization: `Bearer ${token}`,
  },
});

interface Job {
  id: string;
  jobTitle: string;
  companyName: string;
  location: string;
  employerType: string;
  description: string;
  expirationDate: string;
  contactEmail: string;
  roleType: number;
  workplaceType: number;
  preferredCourses: [];
  preferredSkills: [];
  createdOn: string;
  updatedon: string;
}

export default function JobApplication() {
  const params = useParams();
  const jobId = params.jobId as string | undefined;

  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [showApplyModal, setShowApplyModal] = useState(false);
  
  useEffect(() => {
    const fetchJob = async () => {
      try {
        if (!jobId) {
          console.error("JobApplication: missing jobId (prop or route param)");
          toast.error("No job specified");
          setLoading(false);
          return;
        }
        setLoading(true);
        const response = await api.get(`/JobBoard/job-opportunity/${jobId}`);
        const data = response.data;
        console.log("JobApplication fetched:", response.status, data);

        const item: any = data.resultObject;
        if (item) {
          // normalize id field which backend might provide as `_id`
          const normalized: Job = { ...item, id: item.id || item._id };
          setJob(normalized);
        } else {
          setJob(null);
        }
      } catch (error) {
        console.error("Failed to fetch job:", error);
        if (axios.isAxiosError(error)) {
          console.error("Axios error response:", error.response?.status, error.response?.data);
        }
        toast.error("Failed to fetch job");
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [jobId]);

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }
  if (!job) {
    return <div className="text-center py-8">Job not found</div>;
  }

  // Map roleType number to text
  const roleTypeText =
    job.roleType === 1
      ? "Full Time"
      : job.roleType === 2
      ? "Part Time"
      : job.roleType === 3
      ? "Internship"
      : "";

  // Map workplaceType number to text
  const workplaceTypeText =
    job.workplaceType === 1
      ? "Onsite"
      : job.workplaceType === 2
      ? "Remote"
      : job.workplaceType === 3
      ? "Hybrid"
      : "";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-20">
      <div className="container mx-auto px-4">
        {/* Back Button */}
        <div className="max-w-4xl mx-auto mb-6">
          <a
            href="/job-opportunities"
            className="inline-flex items-center text-slate-600 hover:text-slate-800"
          >
            {/* ...icon... */}
            Back to Jobs
          </a>
        </div>
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            <div className="p-8">
              <div className="flex items-start gap-6">
                {/* Job Info */}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h1 className="text-3xl font-bold text-slate-900 mb-2">
                        {job.jobTitle}
                      </h1>
                      <div className="flex items-center gap-2 text-slate-600 mb-4">
                        <span className="font-medium">{job.companyName}</span>
                        <span className="text-slate-300">•</span>
                        <span>{job.location}</span>
                      </div>
                    </div>
                    <div
                      className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${
                        true
                          ? "bg-green-100 text-green-800"
                          : "bg-slate-100 text-slate-800"
                      }`}
                    >
                      Active
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="text-sm text-slate-500 mb-1">
                        Job Type
                      </div>
                      <div className="font-medium text-slate-900 capitalize">
                        {roleTypeText}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="text-sm text-slate-500 mb-1">
                        Workplace Type
                      </div>
                      <div className="font-medium text-slate-900 capitalize">
                        {workplaceTypeText}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="text-sm text-slate-500 mb-1">
                        Contact Email
                      </div>
                      <div className="font-medium text-slate-900">
                        {job.contactEmail}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="text-sm text-slate-500 mb-1">
                        Expriration Date
                      </div>
                      <div className="font-medium text-slate-900">
                        {new Date(job.expirationDate).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  {/* Apply Button (mở modal) */}
                  <button
                    className="w-full md:w-auto px-6 py-3 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700"
                    onClick={() => setShowApplyModal(true)}
                  >
                    Apply Now
                  </button>
                  <ApplyJobModal
                    jobId={job.id}
                    jobTitle={job.jobTitle}
                    open={showApplyModal}
                    onClose={() => setShowApplyModal(false)}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Job Content */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-8">
              <div className="prose prose-slate max-w-none">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                  About the Role
                </h2>
                <div className="text-slate-600">{job.description}</div>
                {job.preferredSkills?.length > 0 && (
                  <>
                    <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">
                      Preferred Skills
                    </h2>
                    <ul className="list-disc list-inside space-y-2 text-slate-600">
                      {job.preferredSkills.map((skill: string, idx: number) => (
                        <li key={idx}>{skill}</li>
                      ))}
                    </ul>
                  </>
                )}
                {job.preferredCourses?.length > 0 && (
                  <>
                    <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">
                      Preferred Courses
                    </h2>
                    <ul className="list-disc list-inside space-y-2 text-slate-600">
                      {job.preferredCourses.map(
                        (course: string, idx: number) => (
                          <li key={idx}>{course}</li>
                        )
                      )}
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
